import type { RefundDay, RevenueLeakage } from '@/api/revenueLeakage'
import { formatCurrency, formatInteger, type FormatOptions } from '@/design'
import { ChartDataTable, ChartFrame, ChartYGrid, labelStep, useChartBox, useChartCursor } from '@/design/chartParts'
import { niceMax } from '@/design/TrendChart'
import { formatDateKey } from '@/lib/filters'

const HEIGHT = 200
const PAD = { left: 56, right: 8, top: 10, bottom: 26 }
const Y_TICKS = 4
const MAX_X_LABELS = 6

/** The tenant day of each draw: the API gives the instant with the tenant's offset, so its date part is the day. */
export function drawDays(draws: RevenueLeakage['draws']): Map<string, string[]> {
  const days = new Map<string, string[]>()
  for (const draw of draws) {
    const day = draw.at.slice(0, 10)
    days.set(day, [...(days.get(day) ?? []), draw.name])
  }
  return days
}

/**
 * Refunds by the day they were issued, one bar a day on one axis, with each draw day
 * shaded behind its bar — refunds follow draws, and this is where that shows. Pointer
 * hover and the arrow keys move a readout with the day's refunds and its draws; the whole
 * series is a visually hidden table too.
 */
export function RefundTimeline({ days, draws, money }: { days: readonly RefundDay[]; draws: RevenueLeakage['draws']; money: FormatOptions }) {
  const box = useChartBox(HEIGHT, PAD)
  const slot = box.innerWidth / Math.max(1, days.length)
  const cursor = useChartCursor(days.length, box.width, (x) => Math.floor((x - box.left) / slot))
  const drawn = drawDays(draws)
  if (days.length === 0) return null

  const max = niceMax(Math.max(0, ...days.map((day) => day.amount)))
  const y = (value: number) => box.bottom - (value / max) * box.innerHeight
  const x = (index: number) => box.left + index * slot
  const barWidth = Math.max(1, slot - 2)
  const every = labelStep(days.length, MAX_X_LABELS)
  const active = cursor.active === null ? null : days[cursor.active]
  const amount = (value: number) => formatCurrency(value, money)
  const title = 'Refunds by the day they were issued, draw days shaded'

  return (
    <ChartFrame
      title={title}
      box={box}
      cursor={cursor}
      className="rl-timeline"
      legendClassName="rl-chart__legend"
      readout={
        active && cursor.active !== null
          ? {
              at: x(cursor.active) + slot / 2,
              content: (
                <>
                  <strong>{formatDateKey(active.day, true)}</strong>
                  <span>Refunded: {amount(active.amount)}</span>
                  <span>Refunds: {formatInteger(active.count, money)}</span>
                  {drawn.has(active.day) ? <span>Draw: {drawn.get(active.day)?.join(', ')}</span> : null}
                </>
              ),
            }
          : null
      }
      legend={
        <>
          <span className="rl-chart__key rl-timeline__key" data-series="refunds">
            Refunds issued
          </span>
          <span className="rl-chart__key rl-timeline__key" data-series="draw">
            Draw day
          </span>
        </>
      }
      table={
        <ChartDataTable
          title={title}
          columns={['Day', 'Refunded', 'Refunds', 'Draws']}
          rows={days.map((day) => ({
            key: day.day,
            name: formatDateKey(day.day, true),
            cells: [amount(day.amount), formatInteger(day.count, money), drawn.get(day.day)?.join(', ') ?? '—'],
          }))}
        />
      }
    >
      {days.map((day, index) =>
        drawn.has(day.day) ? (
          <rect key={`draw-${day.day}`} className="rl-timeline__draw" data-testid="draw-day" x={x(index)} y={box.top} width={slot} height={box.innerHeight} />
        ) : null,
      )}
      <ChartYGrid max={max} ticks={Y_TICKS} left={box.left} right={box.right} y={y} label={(value) => formatCurrency(value, { ...money, compact: true })} />
      {days.map((day, index) =>
        day.amount > 0 ? (
          <rect
            key={day.day}
            className="rl-timeline__bar"
            data-active={cursor.active === index || undefined}
            x={x(index) + (slot - barWidth) / 2}
            y={y(day.amount)}
            width={barWidth}
            height={Math.max(1, box.bottom - y(day.amount))}
            rx={Math.min(2, barWidth / 2)}
          />
        ) : null,
      )}
      {days.map((day, index) =>
        index % every === 0 ? (
          <text key={`x-${day.day}`} className="ci-trend__x" x={x(index) + slot / 2} y={box.height - 8} textAnchor="middle">
            {formatDateKey(day.day)}
          </text>
        ) : null,
      )}
    </ChartFrame>
  )
}
